import type * as pf from '@amirada/lib-shared'
import { NodeType, PfCmdType } from '@amirada/lib-shared'
import type * as tp from '@base/utils/types'
import { def_listLimit, def_listLimitAll, ESchemaContext, ESchemaType, EScreenSize } from '@base/utils/constants'
import {
  ut_copyParams,
  ut_createParamDataString,
  ut_isCartPage,
  ut_isSpecialPage,
  ut_joinPathParts,
  ut_sendToLog,
} from '@base/utils/utils'
import { usePathStore } from '@base/stores/pathStore'
import { useInitStore } from '@base/stores/initStore'
import { useListStore } from '@base/stores/listStore'
import { useMaskStore } from '@base/stores/maskStore'
import { useDetailStore } from '@base/stores/detailStore'
import { usePfinderAPI } from '@base/composables/pfinderAPI'
import { useSearchStore } from '@base/stores/searchStore'

/* ### #5 — Computed Properties spreaden ganzen Store-State OPEN
- **Dateien:** `base/app/composables/list.ts`, `search.ts`, `detail.ts`, `mask.ts`
- **Severity:** MEDIUM | **Aufwand:** 2–3 h

**Problem:** `{ ...indexStore.state.paramData }` erzeugt bei jeder Auswertung ein neues Objekt → jede Änderung an *irgendeiner* Store-Eigenschaft triggert alle Computed Properties neu.

  ```typescript
// Falsch
const listData = computed(() => {
  const paramData = { ...indexStore.state.paramData }  // Neues Objekt, immer dirty
  ...
})

// Fix: Nur relevante Properties referenzieren
const listData = computed(() => {
  const { rcco, lcda, psx, term } = indexStore.state.paramData
  ...
})
``` */

export const useAppIndexStore = defineStore('indexStore', () => {
  const state: tp.IIndexStoreState = reactive ({
    paramData: {
      rcco: '',
      lcda: '',
      namePath: '',
      slugPath: '',
      realpath: '',
      page: '',
      dcl: '',
      redirectpath: '',
      loggedin: false,
      forceloading: false,
      print: false,
      host: '',
      param1: '',
      param2: '',
      param3: '',
      param4: '',
      param5: '',
      param6: '',
      param7: '',
      param8: '',
      param9: '',
      param10: '',
      param11: '',
      param12: '',
      param13: '',
      param14: '',
      param15: '',
      param16: '',
      tab: '',
      tab2: '',
      meta: '',
      rawurl: false,
      iso639: '',
      decSep: '',
      thpSep: '',
      dataLoaded: false,

      queryUrlServer: {
        psx: '',
        term: '',
        sort: '',
        sgd: '',
        rcre1: '',
        rcre2: '',
        rcst: '',
        stage: '',
        date: '',
        dops: '',
        custom: '',
      },
      embeddedInfo: {
        embedded: '',
        protocol: '',
        hostname: '',
        basepath: '',
        basebc: '',
        hidcountry: '',
        hidlanguage: '',
        hidstartpath: '',
        hidstartraw: '',
        embparam1: '',
        embparam2: '',
        embparam3: '',
        embparam4: '',
        embparam5: '',
        embparam6: '',
      },
      userData: {
        firstName: '',
        lastName: '',
        userName: '',
        eMail: '',
        accountId: '',
        gender: '',
        lcd: '',
        userDefined: {},
      },
    },
    paramDataString: '',
    accessGranted: false,
    langguiData: {},
    screenSize: EScreenSize.sc_none,
    headElements: {},
    openGraphElements: {},
    headCanonical: '',
    schemas: {
      schemaProduct: {},
    },
    notifications: [],
    isLoading: false,
    isBot: false,
    userAgent: '',
    cartIsInitialized: false,
    mfxDelim: '',
    emptyValue: '',
    isOffline: false,
    noLocalStorage: false,
    cookie: '',
    liveDate: '',
    productionDate: '',
    userDefined: {},
    currentNode: {
      nodeName: '',
      nodeID: -1,
      meta: {},
      attrs: {},
      cod: '',
      mediaName: '',
      nodeType: NodeType.nt_None,
      dataType: -1,
      desc: '',
      namePath: '',
      slugPath: '',
    },
    newParamData: {
      rcco: '',
      lcda: '',
    },
    scrollPos: {},
    modifiedQueryParams: {
      date: '',
    },
  })
  const appConfig = useAppConfig()
  const runtimeConfig = useRuntimeConfig()
  const initStore = useInitStore()
  const pathStore = usePathStore()
  const listStore = useListStore()
  const maskStore = useMaskStore()
  const detailStore = useDetailStore()
  const searchStore = useSearchStore()
  const pfinderAPI = usePfinderAPI()

  /*
   * Navigation: diff the route against the state and load what changed in one BATCH.
   * Returns false only when that BATCH failed (fetchData answered undefined: overload, server error,
   * no answer). The routing middleware needs to know: during SSR it must not render the page empty
   * with a 200 — a crawler would index that — but answer 503 instead (see routing.global.ts).
   * Every other way out (redirect, nothing to load, loaded) is true.
   */
  async function callInitAction(paramRouter: tp.IParamRouter): Promise<boolean> {
    // ONE bracket around the whole navigation, instead of a setIsLoading(false) at each of its five ways
    // out — none of which covered an exception (14.10.2026, see withLoading).
    return withLoading(() => initAction(paramRouter))
  }

  async function initAction(paramRouter: tp.IParamRouter): Promise<boolean> {
    // Initialize paramData combine with router params
    const paramData: tp.IParamData = initParams(paramRouter)
    if (state.paramData.redirectpath) {
      return true
    } // Redirect URL wrong or path missing

    // Detect differences between state and paramData
    const stateParamData: tp.IParamData = state.paramData
    const paramQueryUrlServer: tp.IParamQueryUrlServer = paramData.queryUrlServer ? paramData.queryUrlServer : {}
    const stateParamQueryUrlServer: tp.IParamQueryUrlServer = stateParamData.queryUrlServer

    const langCountryUserChanged = (
      stateParamData.rcco !== paramData.rcco
      || stateParamData.lcda !== paramData.lcda
      || stateParamQueryUrlServer.rcre1 !== paramQueryUrlServer.rcre1
      || stateParamQueryUrlServer.rcre2 !== paramQueryUrlServer.rcre2
      || stateParamQueryUrlServer.rcst !== paramQueryUrlServer.rcst
      || stateParamQueryUrlServer.stage !== paramQueryUrlServer.stage
      || stateParamQueryUrlServer.date !== paramQueryUrlServer.date
      || stateParamQueryUrlServer.dops !== paramQueryUrlServer.dops
      || !initStore.getInitData(paramData) // InitData not yet loaded
    )
    const pathChanged = (stateParamData.realpath !== paramData.realpath)
    const pageChanged = (stateParamData.page !== paramData.page)
    const psxChanged = (stateParamQueryUrlServer.psx !== paramQueryUrlServer.psx)
    const sortChanged = (
      stateParamQueryUrlServer.sort !== paramQueryUrlServer.sort
    )

    // console.log changes
    /* console.log("LangCountryUser Changed:" + langCountryUserChanged);
      console.log("Path Changed:" + pathChanged + " " + stateParamData.realpath + "->" + paramData.realpath);
      console.log("Page Changed:" + pageChanged);
      console.log("PSX Changed:" + psxChanged);
      console.log("Sort Changed:" + sortChanged);
    */

    // Log to server
    if (runtimeConfig.public.sendToLog) {
      await ut_sendToLog(
        'LangCountryUser Changed:' + langCountryUserChanged + '|'
        + 'Path Changed:' + pathChanged + ' ' + stateParamData.realpath + '->' + paramData.realpath + '|'
        + 'Page Changed:' + pageChanged + '|'
        + 'PSX Changed:' + psxChanged + '|'
        + 'Sort Changed:' + sortChanged,
      )
    }

    // Check User login/Logout
    /* if (stateParamData.userData) {
        if ((stateParamData.userData.username && !stateParamData.loggedin) || (stateParamData.loggedin && stateParamData.userData.username === "")) {
          langCountryUserChanged = true;
          if (appConfig.USERSETTINGS) {
            dispatch("callGetUserDataAction", stateParamData);
          }
        }
      } */

    if (langCountryUserChanged || pathChanged || pageChanged || psxChanged || sortChanged) {
      const cmds: PfCmdType[] = []
      const args: pf.PfCmd<PfCmdType.BATCH>['arg'] = {
        cmds: '',
      }

      if (langCountryUserChanged) {
        if (!initStore.getInitData(paramData)) {
          cmds.push(PfCmdType.INIT)
        }
      }
      if (langCountryUserChanged || pathChanged) {
        if (!pathStore.getPathData(paramData) || langCountryUserChanged) {
          cmds.push(PfCmdType.PATH, PfCmdType.MASK, PfCmdType.LIST, PfCmdType.DETAIL)
          args.slugPath = !paramData.rawurl ? paramData.slugPath : undefined
          args.namePath = paramData.rawurl ? paramData.namePath : undefined
          args.cursor = 0
          args.limit = !paramData.print ? def_listLimit : def_listLimitAll
        }
      }
      else {
        // Depending on nodetype call different functions in specific order to improve performance
        const nodeType: NodeType = state.currentNode.nodeType

        if (nodeType === NodeType.nt_Configuration) {
          if (psxChanged || sortChanged || pageChanged) {
            if (!listStore.getListData(paramData)) {
              cmds.push(PfCmdType.LIST)
            }
            if (pageChanged) {
              deleteScrollPos()
            }
          }
          if (psxChanged) {
            if (!maskStore.getMaskData(paramData)) {
              cmds.push(PfCmdType.MASK)
            }
          }
          if (cmds.length > 0) {
            const cursor: number = paramData.page ? ((Number(paramData.page) - 1) * def_listLimit) || 0 : 0
            args.namePath = state.currentNode.namePath // paramData.namePath
            args.conf = state.currentNode.namePath.split('/').splice(-1)[0] // paramData.namePath.split('/').splice(-1)[0]
            args.cursor = cursor
            args.limit = def_listLimit
          }
        }
      }

      // Only call if not already cached
      if (cmds.length > 0) {
        args.cmds = cmds.join(',')
        const data: tp.IFetchData = {
          paramData,
        }
        const res = await pfinderAPI.fetchData(PfCmdType.BATCH, args, data)

        if (res && res.result) {
          const initData: pf.InitData | undefined = res.result.init
          if (initData) { initStore.setInitData(paramData, initData) }
          if (state.paramData.redirectpath) {
            return true
          } // Redirect URL wrong or path missing

          const pathData: pf.PathNodes | undefined = res.result.path
          if (pathData) {
            const newPathData: tp.IBreadCrumbItem[] | undefined = pathStore.setPathData(paramData, pathData)
            if (state.paramData.redirectpath) {
              return true
            } // Redirect URL wrong or path missing
            // Update paramData namePath for other stores
            const currNode: tp.IBreadCrumbItem | undefined = (newPathData || []).slice(-1)[0]
            paramData.namePath = currNode?.namePath || ''
          // No pathdata node didn't change set namepath to paramdata for other functions
          }
          else {
            paramData.namePath = state.currentNode.namePath
          }

          const detailData: pf.TPfNode | undefined = res.result.detail
          if (detailData) { detailStore.setDetailData(paramData, detailData) }

          const listData: pf.ListNode | undefined = res.result.list
          if (listData) { listStore.setListData(paramData, false, listData) }

          const maskData: pf.MaskNode | undefined = res.result.mask
          if (maskData) { maskStore.setMaskData(paramData, maskData) }
        }
        else {
          // BATCH failed (network/server error → fetchData returned undefined): do NOT advance
          // state.paramData. Otherwise the app "navigates" to an empty page and a subsequent
          // identical navigation is diffed as unchanged and never refetches. Keeping the old
          // paramData means a retry re-diffs and actually reloads.
          return false
        }
      }
    }
    // Update index store after data loaded
    const currNode: tp.IBreadCrumbItem = (pathStore.getPathData(paramData) || []).slice(-1)[0] as tp.IBreadCrumbItem
    if (currNode) {
      setCurrentNode (currNode)
      paramData.namePath = currNode.namePath
      paramData.slugPath = currNode.slugPath
    }

    if (import.meta.server) {
      paramData.dataLoaded = true
    }

    setParamDataMutation(paramData)

    if (paramData.queryUrlServer.term) {
      await searchStore.callSearchData(paramData, 0, def_listLimit)
    }

    // After loading reset meta
    if (langCountryUserChanged || pathChanged) {
      state.headElements = {}
      state.openGraphElements = {}
      state.schemas = {}
    }
    return true
  }

  function initParams(paramRouter: tp.IParamRouter): tp.IParamData {
    const stateParamData: tp.IParamData = state.paramData
    const paramData: tp.IParamData = { ...stateParamData }

    if (paramRouter.query) {
      // ++++++++++++++++++++++++++++++++++++++++
      // Copy query params to store structure
      // For ut_copyParams function it's necessary
      // to set a default empty in initialize
      // ++++++++++++++++++++++++++++++++++++++++
      paramData.queryUrlServer = {}
      ut_copyParams(paramRouter.query, paramData.queryUrlServer, stateParamData.queryUrlServer, true)
      paramData.embeddedInfo = {}
      ut_copyParams(paramRouter.query, paramData.embeddedInfo, stateParamData.embeddedInfo, false)

      paramData.forceloading = !!paramRouter.query.forceloading
      paramData.rawurl = !!paramRouter.query.rawurl
      paramData.page = paramRouter.query.page ? paramRouter.query.page : ''
      paramData.dcl = paramRouter.query.dcl ? paramRouter.query.dcl : ''
      paramData.tab = paramRouter.query.tab ? paramRouter.query.tab : ''
      paramData.tab2 = paramRouter.query.tab2 ? paramRouter.query.tab2 : ''
      paramData.print = paramRouter.query.print ? paramRouter.query.print : false
      paramData.param1 = paramRouter.query.param1 ? paramRouter.query.param1 : ''
      paramData.param2 = paramRouter.query.param2 ? paramRouter.query.param2 : ''
      paramData.param3 = paramRouter.query.param3 ? paramRouter.query.param3 : ''
      paramData.param4 = paramRouter.query.param4 ? paramRouter.query.param4 : ''
      paramData.param5 = paramRouter.query.param5 ? paramRouter.query.param5 : ''
      paramData.param6 = paramRouter.query.param6 ? paramRouter.query.param6 : ''
      paramData.param7 = paramRouter.query.param7 ? paramRouter.query.param7 : ''
      paramData.param8 = paramRouter.query.param8 ? paramRouter.query.param8 : ''
      paramData.param9 = paramRouter.query.param9 ? paramRouter.query.param9 : ''
      paramData.param10 = paramRouter.query.param10 ? paramRouter.query.param10 : ''
      paramData.param11 = paramRouter.query.param11 ? paramRouter.query.param11 : ''
      paramData.param12 = paramRouter.query.param12 ? paramRouter.query.param12 : ''
      paramData.param13 = paramRouter.query.param13 ? paramRouter.query.param13 : ''
      paramData.param14 = paramRouter.query.param14 ? paramRouter.query.param14 : ''
      paramData.param15 = paramRouter.query.param15 ? paramRouter.query.param15 : ''
      paramData.param16 = paramRouter.query.param16 ? paramRouter.query.param16 : ''

      // ⚑ Taken from paramRouter, NOT from useRequestURL() here. This runs inside a store action,
      // and on the server that is not a place a Nuxt composable can bind to. The routing middleware
      // reads it and passes it in as data. It has to be on paramData because customer hooks decide
      // from paramData alone — ut_createParamDataString feeds the stores' cache keys and runs on
      // every read, render included.
      paramData.host = paramRouter.host || ''

      paramData.redirectpath = '' // Clear redirect path on init

      // +++++++++++++++++++++++++++++
      // Analyze path to get rcco and lcda params
      // +++++++++++++++++++++++++++++
      let fullpath: string = decodeURI(paramRouter.path || '') // always without leading slash
      fullpath = fullpath.startsWith('/') ? fullpath.substring(1) : fullpath // Nuxt3 change path comes with leading slash
      const rawurl: boolean = paramData.rawurl

      // embedded info only needs to be set once
      // directly in the first call of CMS, or it is already stored in state.paramData
      let embeddedinfo: tp.IParamEmbedInfo = paramData.embeddedInfo
      if (stateParamData.embeddedInfo) {
        if (stateParamData.embeddedInfo.embedded === '1') {
          embeddedinfo = stateParamData.embeddedInfo
          // directly set embeddedinfo to state
          state.paramData.embeddedInfo = embeddedinfo
        }
      }

      let rcco: string
      let lcda: string
      let slugPath: string = ''
      let namePath: string = ''

      // if basepath is set, strip it from a path
      // fullpath is always without leading slash so add / to compare and substract -1 from length
      const basePath: string = decodeURI(embeddedinfo.basepath || '')
      if (basePath) {
        if (('/' + fullpath).indexOf(basePath) === 0) {
          fullpath = fullpath.substring(basePath.length - 1)
          // the resulting fullpath needs to be again without leading slash as a normal fullpath
          fullpath = fullpath.startsWith('/') ? fullpath.substring(1) : fullpath
        // Base is wrong redirect to default
        }
        else {
          fullpath = ''
        }
      }

      // Only split if not empty
      let splitPath: string[] = []
      if (fullpath) {
        splitPath = fullpath.split('/')
      }

      // +++++++++++++++++++++++++++++++++++++
      // Modify with fix params
      // +++++++++++++++++++++++++++++++++++++
      // Country rcco 1. URL part
      if (embeddedinfo.hidcountry) {
        splitPath.splice(0, 0, embeddedinfo.hidcountry)
      }
      // Country lcda 2. URL part
      if (embeddedinfo.hidlanguage) {
        splitPath.splice(1, 0, embeddedinfo.hidlanguage)
      }
      // path start at position 3
      if (embeddedinfo.hidstartpath) {
        splitPath.splice(2, 0, embeddedinfo.hidstartpath)
      }

      // length > 1 rcco and lcda available
      if (splitPath && splitPath[0] && splitPath[1]) {
        rcco = splitPath[0] // ? splitPath[0] : appConfig.defaultCountry ? appConfig.defaultCountry : ''
        lcda = splitPath[1] // ? splitPath[1] : appConfig.defaultLang ? appConfig.defaultLang : ''

        // if only empty path for redirect then delete
        if (splitPath.length === 3) {
          if (splitPath[2] === ' ') {
            splitPath.splice (-1)
          }
        }

        // length > 2 path available
        if (splitPath.length > 2) {
          const pathAr = splitPath.splice(2)
          // Depending on if names or slugs
          if (!rawurl) {
            slugPath = pathAr.join('/')
          }
          else {
            namePath = pathAr.join('/')
          }
        }
        // use default rcco and lcda if nothing is provided and redirect
      }
      else {
        rcco = appConfig.defaultCountry || ''
        lcda = appConfig.defaultLang || ''
        state.paramData.rcco = rcco
        state.paramData.lcda = lcda
        state.paramData.redirectpath = ' ' // set something to force redirect
      }

      // lcda && rcco check for latin or underscore
      if (!/^[a-z0-9_]*$/.test(lcda) || !/^[a-z0-9_]*$/.test(rcco)) {
        state.paramData.redirectpath = ' ' // set something to force redirect
      }
      else if (['.htm', '.html', '.css', '.js', '.php', '.py', '.asp', '.aspx', '.json', '.xml', '.htaccess'].some(ext => slugPath.endsWith(ext))) {
        state.paramData.redirectpath = ' ' // set something to force redirect
      }

      paramData.rcco = rcco
      paramData.lcda = lcda
      paramData.namePath = namePath || '' // namePath ? namePath : paramData.namePath
      paramData.slugPath = slugPath || ''
      paramData.realpath = paramRouter.path ? decodeURI(paramRouter.path) : ''

      // new paramData coming up route
      state.newParamData.rcco = rcco
      state.newParamData.lcda = lcda
    }
    return paramData
  }

  // Setters

  function setParamDataMutation(paramData: Partial<tp.IParamData>): void {
    // URL parameters
    if (paramData.rcco !== undefined) { state.paramData.rcco = paramData.rcco }
    if (paramData.lcda !== undefined) { state.paramData.lcda = paramData.lcda }
    if (paramData.iso639 !== undefined) { state.paramData.iso639 = paramData.iso639 }
    if (paramData.decSep !== undefined) { state.paramData.decSep = paramData.decSep }
    if (paramData.thpSep !== undefined) { state.paramData.thpSep = paramData.thpSep }
    if (paramData.namePath !== undefined) { state.paramData.namePath = paramData.namePath }
    if (paramData.slugPath !== undefined) { state.paramData.slugPath = paramData.slugPath }
    if (paramData.realpath !== undefined) { state.paramData.realpath = paramData.realpath }

    if (paramData.page !== undefined) { state.paramData.page = paramData.page }
    if (paramData.tab !== undefined) { state.paramData.tab = paramData.tab }
    if (paramData.tab2 !== undefined) { state.paramData.tab2 = paramData.tab2 }
    if (paramData.meta !== undefined) { state.paramData.meta = paramData.meta }
    if (paramData.redirectpath !== undefined) { state.paramData.redirectpath = paramData.redirectpath }

    if (paramData.queryUrlServer) {
      ut_copyParams(paramData.queryUrlServer, state.paramData.queryUrlServer, state.paramData.queryUrlServer)
    }
    if (paramData.embeddedInfo) {
      ut_copyParams(paramData.embeddedInfo, state.paramData.embeddedInfo, state.paramData.embeddedInfo)
    }

    if (paramData.userData) {
      ut_copyParams(paramData.userData, state.paramData.userData, state.paramData.userData)
    }

    // dataLoaded
    if (paramData.dataLoaded !== undefined) { state.paramData.dataLoaded = paramData.dataLoaded }

    // login
    if (paramData.loggedin !== undefined) { state.paramData.loggedin = paramData.loggedin }

    // Force Loading if parallel request because login running
    if (paramData.forceloading !== undefined) { state.paramData.forceloading = paramData.forceloading }

    // Force Loading if parallel request because login running
    if (paramData.print !== undefined) { state.paramData.print = paramData.print }

    // raw URL using names instead of slug
    if (paramData.rawurl !== undefined) { state.paramData.rawurl = paramData.rawurl }

    if (paramData.host !== undefined) { state.paramData.host = paramData.host }

    if (paramData.param1 !== undefined) { state.paramData.param1 = paramData.param1 }
    if (paramData.param2 !== undefined) { state.paramData.param2 = paramData.param2 }
    if (paramData.param3 !== undefined) { state.paramData.param3 = paramData.param3 }
    if (paramData.param4 !== undefined) { state.paramData.param4 = paramData.param4 }
    if (paramData.param5 !== undefined) { state.paramData.param5 = paramData.param5 }
    if (paramData.param6 !== undefined) { state.paramData.param6 = paramData.param6 }
    if (paramData.param7 !== undefined) { state.paramData.param7 = paramData.param7 }
    if (paramData.param8 !== undefined) { state.paramData.param8 = paramData.param8 }
    if (paramData.param9 !== undefined) { state.paramData.param9 = paramData.param9 }
    if (paramData.param10 !== undefined) { state.paramData.param10 = paramData.param10 }
    if (paramData.param11 !== undefined) { state.paramData.param11 = paramData.param11 }
    if (paramData.param12 !== undefined) { state.paramData.param12 = paramData.param12 }
    if (paramData.param13 !== undefined) { state.paramData.param13 = paramData.param13 }
    if (paramData.param14 !== undefined) { state.paramData.param14 = paramData.param14 }
    if (paramData.param15 !== undefined) { state.paramData.param15 = paramData.param15 }
    if (paramData.param16 !== undefined) { state.paramData.param16 = paramData.param16 }

    // Object assign not working because of undefined
    /* const embeddedInfo: tp.IParamEmbedInfo = Object.assign(state.paramData.embeddedInfo, paramData.embeddedInfo)
    const queryUrlServer: tp.IParamQueryUrlServer = Object.assign(state.paramData.queryUrlServer, paramData.queryUrlServer)
    const userData: tp.IUserData = Object.assign(state.paramData.userData, paramData.userData)
    Object.assign(state.paramData, paramData)
    state.paramData.embeddedInfo = embeddedInfo
    state.paramData.queryUrlServer = queryUrlServer
    state.paramData.userData = userData */

    // create string combination of cache relevant parameters
    state.paramDataString = ut_createParamDataString(state.paramData)
  }

  function setModifiedQueryParams(queryParams: Partial<tp.IParamQueryServer>): void {
    // Seed keys not yet present, otherwise ut_copyParams (which iterates the EXISTING keys of
    // modifiedQueryParams) would silently drop a newly-introduced param. Clearing via {} still works:
    // nothing to seed, and ut_copyParams empties the existing keys as before.
    for (const key in queryParams) {
      if (!(key in state.modifiedQueryParams)) {
        (state.modifiedQueryParams as Record<string, unknown>)[key] = ''
      }
    }
    ut_copyParams(queryParams, state.modifiedQueryParams, state.modifiedQueryParams, true)
  }

  function setCurrentNode(breadCrumbItem: tp.IBreadCrumbItem): void {
    state.currentNode = breadCrumbItem
  }

  /*
   * The global loading state (the overlay with the Wilo loading icon, the progress bar, the locked
   * settings) is COUNTED, not switched (14.10.2026, ai_memory/project_loading_counter_20261014.md).
   *
   * It used to be one bool with many owners — the navigation, the search, every wilo fetchData, the cart
   * icons, LCC, the pdf dialog — and whoever finished first switched it off while another was still
   * loading; `noLoading` was the compulsory workaround for every caller that bracketed requests itself.
   * And a bracket without `finally` left the whole page locked under the overlay after an early return
   * or an exception (CartIcons did).
   *
   * `state.isLoading` stays a bool for its readers (= count > 0). The only way to change it is
   * withLoading: +1, run, −1 in `finally` — nothing can leave it on, nothing can take it away early.
   * The count is per store instance, i.e. per request on the server.
   */
  let loadingCount: number = 0

  async function withLoading<T>(fn: () => Promise<T>): Promise<T> {
    loadingCount++
    state.isLoading = true
    try {
      return await fn()
    }
    finally {
      loadingCount = Math.max(0, loadingCount - 1)
      state.isLoading = loadingCount > 0
    }
  }

  function setAccessGranted(value: boolean): void {
    state.accessGranted = value
  }

  function setCartIsInitialized(value: boolean): void {
    state.cartIsInitialized = value
  }

  function setHeadElements(headElements: tp.IHeadElements): void {
    state.headElements = headElements
  }

  function setOpenGraphElements(openGraphElements: tp.IOpenGraphElements): void {
    state.openGraphElements = openGraphElements
  }

  function setSchemas(schemas: tp.ISchemas): void {
    state.schemas = schemas
  }

  function setScrollPos(key: string, pos: { top: number, left: number }): void {
    state.scrollPos[key] = pos
  }

  function setScreenSize(screenSize: EScreenSize): void {
    state.screenSize = screenSize
  }

  function deleteScrollPos() {
    state.scrollPos = {}
  }

  function setNotification(notification: tp.INotification): void {
    if (!state.notifications.find(item => item.id === notification.id)) {
      state.notifications.push(notification)
    }
  }

  function deleteNotification(id: string): void {
    state.notifications = state.notifications.filter(item => item.id !== id)
  }

  function setUserDefined(key: string, value: any): void {
    state.userDefined[key] = value
  }

  // GETTERS
  const getCountryLangPath = computed((): string => {
    const basePath: string[] = (state.paramData.embeddedInfo.basepath || '').split('/') || []
    const countryLangPath: string[] = [state.paramData.rcco, state.paramData.lcda]
    return '/' + ut_joinPathParts([...basePath, ...countryLangPath])
  })

  const getFullPath = computed((): string => {
    return getCountryLangPath.value + '/' + state.paramData.slugPath
  })

  const getAbsolutePath = computed((): string => {
    let url: string = [runtimeConfig.public.dataHost || '', runtimeConfig.public.pfinderService || ''].join('/')
    if (state.paramData.embeddedInfo) {
      if (state.paramData.embeddedInfo.embedded) {
        url = 'https://' + state.paramData.embeddedInfo.hostname || ''
      }
    }
    url += state.paramData.realpath
    return url
  })

  const getRootPath = computed(() => {
    return getCountryLangPath.value + '/' + getHiddenRootPathSlugs.value
  })

  const getHiddenRootPathSlugs = computed(() => {
    let res: string = ''
    const breadCrumbs: tp.IBreadCrumbItem[] | undefined = pathStore.getPathData(state.paramData)
    if (breadCrumbs) {
      res = breadCrumbs[0]?.meta?.slug || ''
      // check for hidden root path and add to root path
      if (state.paramData.embeddedInfo) {
        if (state.paramData.embeddedInfo.hidstartpath) {
          const splitRoot = state.paramData.embeddedInfo.hidstartpath.split('/')
          if (splitRoot.length > 1) {
            for (let i = 1; i <= splitRoot.length - 1; i++) {
              if (breadCrumbs.length >= i) {
                res += '/' + breadCrumbs[i]?.meta?.slug
              }
            }
          }
        }
      }
    }
    return res
  })

  const getIsResultNode = computed((): boolean => {
    switch (state.currentNode.nodeType) {
      case NodeType.nt_root:
      case NodeType.nt_WebClass:
      case NodeType.nt_Configuration:
      case NodeType.nt_None:
        return false
      default:
        return true
    }
  })

  const getIsConfig = computed((): boolean => {
    return state.currentNode.nodeType === NodeType.nt_Configuration
  })

  const getIsNavigation = computed((): boolean => {
    return (state.currentNode.nodeType === NodeType.nt_root || state.currentNode.nodeType === NodeType.nt_WebClass)
  })

  const getIsSpecialPage = computed((): boolean => {
    let res: boolean = false
    if (state.currentNode.nodeType === NodeType.nt_None) {
      res = ut_isSpecialPage(state.currentNode.nodeName)
    }
    return res
  })

  const getIsCartPage = computed((): boolean => {
    let res: boolean = false
    if (state.currentNode.nodeType === NodeType.nt_None) {
      res = ut_isCartPage(state.currentNode.nodeName)
    }
    return res
  })

  const getCurrentConfig = computed((): tp.IBreadCrumbItem | undefined => {
    if (getIsConfig.value) {
      return state.currentNode
    }
    else if (getIsResultNode.value) {
      const pathData: tp.IBreadCrumbItem[] | undefined = pathStore.getPathData(state.paramData)
      if (pathData) {
        return pathData[pathData.length - 2]
      }
    }
  })

  /**
   * A sub-object of the product schema, stamped with its type — but only if it says anything.
   * Asking whether the field EXISTS was the bug: an empty object is truthy, so a product without
   * price data still shipped `"offers": {"@type": "Offer"}`, and an Offer without price and
   * priceCurrency is an error in Google's rich results. The same held for brand and the four
   * measures.
   * `@type` is skipped when judging: a stamped object would otherwise look filled the next time
   * round. That cannot happen any more anyway, because a copy is returned instead of the stored
   * object being written to — a computed had no business mutating the state it reads.
   */
  function schemaPart<T extends object>(part: T | undefined, type: ESchemaType): T | undefined {
    if (!part) {
      return undefined
    }
    const saysSomething: boolean = Object.entries(part).some(([key, value]) => key !== '@type' && value !== '' && value !== undefined)
    return saysSomething ? { ...part, '@type': type } : undefined
  }

  const getJsonLD = computed(() => {
    const source: tp.ISchemaProduct = state.schemas.schemaProduct || {}
    if (!source.name) {
      return source
    }
    // Undefined fields drop out on their own: JSON.stringify omits them, so an empty part leaves
    // no trace in the markup.
    const schemaProduct: tp.ISchemaProduct = {
      ...source,
      '@context': ESchemaContext.sch_context,
      '@type': ESchemaType.sch_type_product,
      'aggregateRating': schemaPart(source.aggregateRating, ESchemaType.sch_type_aggregateRating),
      'offers': schemaPart(source.offers, ESchemaType.sch_type_offer),
      'brand': schemaPart(source.brand, ESchemaType.sch_type_brand),
      'depth': schemaPart(source.depth, ESchemaType.sch_type_quantitive_value),
      'width': schemaPart(source.width, ESchemaType.sch_type_quantitive_value),
      'height': schemaPart(source.height, ESchemaType.sch_type_quantitive_value),
      'weight': schemaPart(source.weight, ESchemaType.sch_type_quantitive_value),
    }
    // Last guard, one level flatter than schemaPart: an empty string or an empty list is not a
    // missing value to a consumer, it is a malformed one. Producers should not set them in the
    // first place, but nothing that reaches here empty has any business in the markup.
    return Object.fromEntries(
      Object.entries(schemaProduct).filter(([, value]) => value !== '' && value !== undefined && !(Array.isArray(value) && !value.length)),
    ) as tp.ISchemaProduct
  })

  return {
    state,
    callInitAction,
    setParamDataMutation,
    withLoading,
    setHeadElements,
    setOpenGraphElements,
    setSchemas,
    setAccessGranted,
    setCartIsInitialized,
    setScrollPos,
    setScreenSize,
    setModifiedQueryParams,
    deleteScrollPos,
    setNotification,
    deleteNotification,
    setUserDefined,
    getCountryLangPath,
    getFullPath,
    getAbsolutePath,
    getRootPath,
    getHiddenRootPathSlugs,
    getIsResultNode,
    getIsConfig,
    getIsNavigation,
    getIsSpecialPage,
    getIsCartPage,
    getCurrentConfig,
    getJsonLD,
  }
})
